import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useParams, useNavigate, Link, useSearchParams } from "react-router"
import { useTranslation } from "react-i18next"

import { EVAL_CASE_LIMIT } from "@/constants"
import { useAgentHistories } from "@/hooks/useAgentHistories"
import {
  useTriggerEvaluationApi,
  makeTriggerEvaluationRequest,
} from "@/hooks/api/eval"
import { Agent, AgentType } from "@/types"
import { CaseEvaluatorAssignment, EvaluationCaseEvaluatorPair } from "@/types"
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbSeparator,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Sheet,
  SheetContent,
} from "@f2e/ui/components/base"
import useToast from "@/hooks/useToast"
import { useWindowSize } from "@/hooks"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"

import {
  useEvaluationData,
  useEvaluationPolling,
  useCsvUpload,
  useCaseManagement,
  Evaluator,
  EvaluationCase,
  MergedEvaluationCase,
  EvaluatorModal,
  EvaluationTableHeader,
  EvaluationCaseTable,
  CaseDetailModal,
  AddCaseModal,
  CsvPreviewModal,
  DeleteConfirmModal,
  ManageCaseLabelsModal,
  ALL_EVALUATORS_STORAGE_VALUE,
  STORAGE_KEYS,
} from "@/components/Evaluation"
import AgentBreadcrumbSelector from "./components/AgentBreadcrumbSelector"
import PageBreadcrumbSelector from "./components/PageBreadcrumbSelector"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"

export interface EvaluationTablePageProps {
  showNavigation?: boolean
  agents?: Agent[]
  currentAgent?: Agent
}

interface SkippedEvaluationPairSchema {
  case_id: string
  evaluator_id: string
  reason: string
}

const getPairKey = (pair: EvaluationCaseEvaluatorPair) =>
  `${pair.caseId}:${pair.evaluatorId}`

export const getPollingTargetsFromTriggerResponse = (
  caseEvaluatorPairs: EvaluationCaseEvaluatorPair[],
  agentHistoryId: string,
  skippedPairs: SkippedEvaluationPairSchema[] = []
) => {
  const skippedPairKeys = new Set(
    skippedPairs
      .filter((pair) => pair.reason !== "already_running")
      .map((pair) => `${pair.case_id}:${pair.evaluator_id}`)
  )

  return caseEvaluatorPairs
    .filter((pair) => !skippedPairKeys.has(getPairKey(pair)))
    .map((pair) => ({
      ...pair,
      agentHistoryId,
    }))
}

export const getSelectedCaseWithLoadedAssignments = (
  tableCases: MergedEvaluationCase[],
  selectedCaseId: string | null,
  caseEvaluatorInfosByCase: Record<string, CaseEvaluatorAssignment[]>
) => {
  const matchingCase =
    tableCases.find((caseData) => caseData.id === selectedCaseId) || null
  return matchingCase &&
    Object.prototype.hasOwnProperty.call(
      caseEvaluatorInfosByCase,
      matchingCase.id
    )
    ? matchingCase
    : null
}

const EvaluationTablePage = ({
  showNavigation = true,
  agents: providedAgents,
  currentAgent: providedCurrentAgent,
}: EvaluationTablePageProps) => {
  const { workspaceId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t, ready } = useTranslation()
  const { alertToast } = useToast()

  // Main data management hook
  const {
    agents,
    currentAgent,
    evaluators,
    selectedEvaluator,
    setSelectedEvaluator,
    labels,
    selectedLabelIds,
    setSelectedLabelIds,
    refreshLabels,
    getCaseSearchParams,
    selectedAgentHistory,
    setSelectedAgentHistory,
    totalCaseCount,
    setCases,
    caseEvaluatorInfosByCase,
    setCaseEvaluatorInfosByCase,
    results,
    mergedCases,
    loading,
    error,
    fetchCases,
    fetchEvaluationResultsForScope,
    handleApiErrors,
    refreshEvaluators,
  } = useEvaluationData({
    agents: providedAgents,
    currentAgent: providedCurrentAgent,
  })

  const tableCases = useMemo(
    () =>
      selectedEvaluator
        ? mergedCases.filter((caseData) => caseData.selectedEvaluatorAssigned)
        : mergedCases,
    [mergedCases, selectedEvaluator]
  )

  const tableResults = useMemo(() => {
    if (!selectedEvaluator || !selectedAgentHistory?.id) return []
    const caseIds = new Set(tableCases.map((caseData) => caseData.id))
    return results.filter(
      (result) =>
        caseIds.has(result.evaluationCaseId) &&
        result.evaluatorId === selectedEvaluator.id &&
        result.agentHistoryId === selectedAgentHistory.id
    )
  }, [results, selectedEvaluator, selectedAgentHistory, tableCases])

  // Agent history dropdown state
  const agentHistoryState = useAgentHistories(currentAgent?.id || "", {
    initialLimit: 10,
  })

  // Update selected agent history when dropdown selection changes
  useEffect(() => {
    if (agentHistoryState.currentHistory) {
      setSelectedAgentHistory(agentHistoryState.currentHistory)
    } else {
      setSelectedAgentHistory(null)
    }
  }, [agentHistoryState.currentHistory, setSelectedAgentHistory])

  // Trigger evaluation API
  const { fetchFn: triggerEvaluation } = useTriggerEvaluationApi()

  // CSV upload logic
  const {
    showCsvDialog,
    csvPreviewRows,
    csvPreviewErrors,
    csvFile,
    csvUploading,
    handleOpenCsvDialog,
    handleUploadCSV,
    handleConfirmCsvUpload,
    handleCancelCsvPreview,
    csvWouldExceedLimit,
  } = useCsvUpload(
    totalCaseCount,
    fetchCases,
    selectedEvaluator?.id,
    getCaseSearchParams
  )

  // Case management logic
  const {
    showAddCaseModal,
    setShowAddCaseModal,
    addCaseForm,
    addCaseError,
    addCaseLoading,
    handleAddCaseChange,
    handleAddCaseSubmit,
    showDeleteConfirm,
    deleteLoading,
    handleDeleteSelected,
    handleConfirmDelete,
    handleCancelDelete,
    selectedIds,
    allSelected,
    isIndeterminate,
    handleSelectAll,
    handleSelectRow,
    handleCopySelected,
  } = useCaseManagement(
    tableCases,
    workspaceId,
    fetchCases,
    getCaseSearchParams
  )

  // Evaluator modal state
  const [showEvaluatorModal, setShowEvaluatorModal] = useState(false)
  const [showLabelsModal, setShowLabelsModal] = useState(false)
  const { width: viewportWidth } = useWindowSize()
  const isDetailDrawerMode = viewportWidth < 1024

  // Persistent case detail state
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null)
  const [isDetailOpenOnMobile, setIsDetailOpenOnMobile] = useState(false)
  const [hasDetailUnsavedChanges, setHasDetailUnsavedChanges] = useState(false)
  const [isDetailSaving, setIsDetailSaving] = useState(false)
  const [pendingCaseSelection, setPendingCaseSelection] = useState<{
    caseId: string
  } | null>(null)
  const [pendingCloseDetail, setPendingCloseDetail] = useState(false)
  const [pendingEvaluatorSelection, setPendingEvaluatorSelection] = useState<
    Evaluator | null | undefined
  >(undefined)
  const [showUnsavedDetailDialog, setShowUnsavedDetailDialog] = useState(false)
  const focusCaseId = searchParams.get("case_id")
  const shouldClearEvalFilters = searchParams.get("clear_eval_filters") === "1"

  const selectedCase = useMemo(
    () =>
      getSelectedCaseWithLoadedAssignments(
        tableCases,
        selectedCaseId,
        caseEvaluatorInfosByCase
      ),
    [caseEvaluatorInfosByCase, tableCases, selectedCaseId]
  )

  const currentDetailEvaluationResult = useMemo(() => {
    if (!selectedCase || !selectedEvaluator) return undefined
    return tableResults.find(
      (result) => result.evaluationCaseId === selectedCase.id
    )
  }, [selectedCase, selectedEvaluator, tableResults])

  const detailPollingTarget = useMemo(() => {
    if (!selectedCase || !selectedEvaluator || !selectedAgentHistory?.id)
      return null
    return {
      caseId: selectedCase.id,
      evaluatorId: selectedEvaluator.id,
      agentHistoryId: selectedAgentHistory.id,
    }
  }, [selectedCase, selectedEvaluator, selectedAgentHistory])

  // Polling logic
  const { runAllLoading, setRunAllLoading, startPollingTargets } =
    useEvaluationPolling(
      tableCases,
      selectedEvaluator,
      selectedAgentHistory,
      results,
      fetchEvaluationResultsForScope,
      detailPollingTarget
    )

  // Track Run Selected separately so only the originating button shows a spinner
  const [runSelectedLoading, setRunSelectedLoading] = useState(false)
  useEffect(() => {
    if (!runAllLoading) setRunSelectedLoading(false)
  }, [runAllLoading])

  useEffect(() => {
    if (!selectedCaseId) return
    if (!tableCases.some((c) => c.id === selectedCaseId)) {
      setSelectedCaseId(null)
      setIsDetailOpenOnMobile(false)
      setHasDetailUnsavedChanges(false)
    }
  }, [tableCases, selectedCaseId])

  const buildRunnablePairs = useCallback(
    (targetCases: MergedEvaluationCase[]) => {
      if (!selectedEvaluator) return []
      return targetCases
        .filter((caseData) =>
          (caseData.assignedEvaluators || []).some(
            (assignment) => assignment.evaluatorId === selectedEvaluator.id
          )
        )
        .map((caseData) => ({
          caseId: caseData.id,
          evaluatorId: selectedEvaluator.id,
        }))
    },
    [selectedEvaluator]
  )

  const selectedCases = useMemo(
    () => tableCases.filter((caseData) => selectedIds.includes(caseData.id)),
    [tableCases, selectedIds]
  )

  const runnablePairCount = useMemo(
    () => buildRunnablePairs(tableCases).length,
    [buildRunnablePairs, tableCases]
  )

  const selectedRunnablePairCount = useMemo(
    () => buildRunnablePairs(selectedCases).length,
    [buildRunnablePairs, selectedCases]
  )

  // Navigation handlers
  const handleAgentSelect = useCallback(
    (agent: Agent) => {
      navigate(`/workspaces/${workspaceId}/agents/${agent.id}?tab=evaluation`)
    },
    [workspaceId, navigate]
  )

  const handleCreateNewAgent = useCallback(
    (agentType: AgentType) => {
      navigate(`/workspaces/${workspaceId}/agents/new?type=${agentType}`)
    },
    [workspaceId, navigate]
  )

  const applyEvaluatorSelection = useCallback(
    (ev: Evaluator | null) => {
      localStorage.setItem(
        STORAGE_KEYS.SELECTED_EVALUATOR,
        ev?.id || ALL_EVALUATORS_STORAGE_VALUE
      )
      setSelectedEvaluator(ev)
    },
    [setSelectedEvaluator]
  )

  const handleSelectEvaluator = useCallback(
    (ev: Evaluator | null) => {
      if ((ev?.id || null) === (selectedEvaluator?.id || null)) return

      if (hasDetailUnsavedChanges) {
        setPendingCaseSelection(null)
        setPendingCloseDetail(false)
        setPendingEvaluatorSelection(ev)
        setShowUnsavedDetailDialog(true)
        return
      }

      applyEvaluatorSelection(ev)
    },
    [applyEvaluatorSelection, hasDetailUnsavedChanges, selectedEvaluator]
  )

  const applyCaseSelection = useCallback(
    (caseId: string) => {
      setSelectedCaseId(caseId)
      setHasDetailUnsavedChanges(false)
      if (isDetailDrawerMode) {
        setIsDetailOpenOnMobile(true)
      }
    },
    [isDetailDrawerMode]
  )

  useEffect(() => {
    if (!focusCaseId || !shouldClearEvalFilters) return
    localStorage.setItem(
      STORAGE_KEYS.SELECTED_EVALUATOR,
      ALL_EVALUATORS_STORAGE_VALUE
    )
    setSelectedEvaluator(null)
    setSelectedLabelIds([])
  }, [
    focusCaseId,
    setSelectedEvaluator,
    setSelectedLabelIds,
    shouldClearEvalFilters,
  ])

  useEffect(() => {
    if (!focusCaseId) return
    const targetCase = tableCases.find(
      (caseData) => caseData.id === focusCaseId
    )
    if (!targetCase) return
    if (
      !Object.prototype.hasOwnProperty.call(
        caseEvaluatorInfosByCase,
        focusCaseId
      )
    )
      return

    applyCaseSelection(focusCaseId)
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.delete("case_id")
    nextSearchParams.delete("clear_eval_filters")
    setSearchParams(nextSearchParams, { replace: true })
  }, [
    applyCaseSelection,
    caseEvaluatorInfosByCase,
    focusCaseId,
    searchParams,
    setSearchParams,
    tableCases,
  ])

  const handleCloseCaseDetail = useCallback(() => {
    if (hasDetailUnsavedChanges) {
      setPendingCaseSelection(null)
      setPendingCloseDetail(true)
      setPendingEvaluatorSelection(undefined)
      setShowUnsavedDetailDialog(true)
      return
    }

    setSelectedCaseId(null)
    setIsDetailOpenOnMobile(false)
    setHasDetailUnsavedChanges(false)
  }, [hasDetailUnsavedChanges])

  const handleOpenCaseDetail = useCallback(
    (caseData: EvaluationCase) => {
      if (selectedCaseId === caseData.id) {
        if (isDetailDrawerMode) {
          setIsDetailOpenOnMobile(true)
        } else {
          handleCloseCaseDetail()
        }
        return
      }

      if (hasDetailUnsavedChanges) {
        setPendingCaseSelection({ caseId: caseData.id })
        setPendingCloseDetail(false)
        setPendingEvaluatorSelection(undefined)
        setShowUnsavedDetailDialog(true)
        return
      }

      applyCaseSelection(caseData.id)
    },
    [
      applyCaseSelection,
      handleCloseCaseDetail,
      hasDetailUnsavedChanges,
      isDetailDrawerMode,
      selectedCaseId,
    ]
  )

  const handleCancelCaseDetail = useCallback(() => {
    setSelectedCaseId(null)
    setIsDetailOpenOnMobile(false)
    setHasDetailUnsavedChanges(false)
  }, [])

  const handleConfirmDiscardDetailChanges = useCallback(() => {
    if (pendingEvaluatorSelection !== undefined) {
      applyEvaluatorSelection(pendingEvaluatorSelection)
      setHasDetailUnsavedChanges(false)
    } else if (pendingCaseSelection) {
      applyCaseSelection(pendingCaseSelection.caseId)
    } else if (pendingCloseDetail) {
      setSelectedCaseId(null)
      setIsDetailOpenOnMobile(false)
      setHasDetailUnsavedChanges(false)
    }

    setPendingCaseSelection(null)
    setPendingCloseDetail(false)
    setPendingEvaluatorSelection(undefined)
    setShowUnsavedDetailDialog(false)
  }, [
    applyCaseSelection,
    applyEvaluatorSelection,
    pendingCaseSelection,
    pendingCloseDetail,
    pendingEvaluatorSelection,
  ])

  const handleCancelDiscardDetailChanges = useCallback(() => {
    setPendingCaseSelection(null)
    setPendingCloseDetail(false)
    setPendingEvaluatorSelection(undefined)
    setShowUnsavedDetailDialog(false)
  }, [])

  // Enhanced case change handler that updates local state
  const handleCaseChangeEnhanced = useCallback(
    (
      caseId: string,
      updated: Partial<EvaluationCase> & {
        rubric?: string
        assignedEvaluators?: CaseEvaluatorAssignment[]
      },
      _evaluatorId?: string
    ) => {
      setCases((prev) =>
        prev.map((c) =>
          c.id === caseId
            ? {
                ...c,
                // Handle input field updates
                input: updated.input !== undefined ? updated.input : c.input,
                // Handle expectedOutput field updates
                expectedOutput:
                  updated.expectedOutput !== undefined
                    ? updated.expectedOutput
                    : c.expectedOutput,
                // Handle note field updates
                note: updated.note !== undefined ? updated.note : c.note,
                // Handle attachments field updates
                attachments:
                  updated.attachments !== undefined
                    ? updated.attachments
                    : c.attachments,
                labels:
                  updated.labels !== undefined ? updated.labels : c.labels,
              }
            : c
        )
      )
      if (updated.assignedEvaluators !== undefined) {
        setCaseEvaluatorInfosByCase((prev) => ({
          ...prev,
          [caseId]: updated.assignedEvaluators || [],
        }))
      }
      if (updated.labels !== undefined && selectedLabelIds.length > 0) {
        void fetchCases({ searchParams: getCaseSearchParams() })
      }
    },
    [
      setCases,
      setCaseEvaluatorInfosByCase,
      selectedLabelIds.length,
      fetchCases,
      getCaseSearchParams,
    ]
  )

  // Run All button handler
  const handleRunAll = useCallback(() => {
    const caseEvaluatorPairs = buildRunnablePairs(mergedCases)
    if (!caseEvaluatorPairs.length || !selectedAgentHistory?.id || !workspaceId)
      return

    setRunAllLoading(true)

    void (async () => {
      try {
        const requestPayload = makeTriggerEvaluationRequest(
          caseEvaluatorPairs,
          selectedAgentHistory?.id
        )
        const triggerResponse = await triggerEvaluation({
          jsonBody: requestPayload,
        })
        const pollingTargets = getPollingTargetsFromTriggerResponse(
          caseEvaluatorPairs,
          selectedAgentHistory.id,
          triggerResponse?.data?.skipped_pairs
        )
        if (pollingTargets.length) {
          startPollingTargets(pollingTargets)
        } else {
          setRunAllLoading(false)
        }
      } catch (err) {
        console.error("Failed to run all case evaluations:", err)
        setRunAllLoading(false)
        alertToast({
          ...getUserFacingApiErrorToastProps(err, t, {
            title: t(
              "evaluation.evaluationTable.message.runEvaluationError.title"
            ),
            fallbackDescription: t(
              "evaluation.evaluationTable.message.runEvaluationError.description"
            ),
          }),
        })
      }
    })()
  }, [
    mergedCases,
    buildRunnablePairs,
    selectedAgentHistory,
    workspaceId,
    triggerEvaluation,
    startPollingTargets,
    setRunAllLoading,
    alertToast,
    t,
  ])

  // Run Selected button handler
  const handleRunSelected = useCallback(() => {
    const caseEvaluatorPairs = buildRunnablePairs(selectedCases)
    if (
      !selectedIds.length ||
      !caseEvaluatorPairs.length ||
      !selectedAgentHistory?.id ||
      !workspaceId
    )
      return

    setRunSelectedLoading(true)
    setRunAllLoading(true)

    void (async () => {
      try {
        const requestPayload = makeTriggerEvaluationRequest(
          caseEvaluatorPairs,
          selectedAgentHistory?.id
        )
        const triggerResponse = await triggerEvaluation({
          jsonBody: requestPayload,
        })
        const pollingTargets = getPollingTargetsFromTriggerResponse(
          caseEvaluatorPairs,
          selectedAgentHistory.id,
          triggerResponse?.data?.skipped_pairs
        )
        if (pollingTargets.length) {
          startPollingTargets(pollingTargets)
        } else {
          setRunSelectedLoading(false)
          setRunAllLoading(false)
        }
      } catch (err) {
        console.error("Failed to run selected case evaluations:", err)
        setRunSelectedLoading(false)
        setRunAllLoading(false)
        alertToast({
          ...getUserFacingApiErrorToastProps(err, t, {
            title: t(
              "evaluation.evaluationTable.message.runEvaluationError.title"
            ),
            fallbackDescription: t(
              "evaluation.evaluationTable.message.runEvaluationError.description"
            ),
          }),
        })
      }
    })()
  }, [
    selectedIds,
    selectedCases,
    buildRunnablePairs,
    selectedAgentHistory,
    workspaceId,
    triggerEvaluation,
    startPollingTargets,
    setRunAllLoading,
    alertToast,
    t,
  ])

  // Run Single Case handler
  const handleRunSingleCase = useCallback(
    async (caseId: string, evaluatorId: string, agentHistoryId: string) => {
      if (!workspaceId) return
      try {
        const requestPayload = makeTriggerEvaluationRequest(
          [{ caseId, evaluatorId }],
          agentHistoryId
        )
        const triggerResponse = await triggerEvaluation({
          jsonBody: requestPayload,
        })
        const pollingTargets = getPollingTargetsFromTriggerResponse(
          [{ caseId, evaluatorId }],
          agentHistoryId,
          triggerResponse?.data?.skipped_pairs
        )
        if (pollingTargets.length) startPollingTargets(pollingTargets)
      } catch (err) {
        console.error("Failed to run single case evaluation:", err)
        alertToast({
          ...getUserFacingApiErrorToastProps(err, t, {
            title: t(
              "evaluation.evaluationTable.message.singleCaseError.title"
            ),
            fallbackDescription: t(
              "evaluation.evaluationTable.message.singleCaseError.description"
            ),
          }),
        })
      }
    },
    [workspaceId, triggerEvaluation, startPollingTargets, alertToast, t]
  )

  // Error handling effect
  useEffect(() => {
    handleApiErrors()
  }, [handleApiErrors])

  // Scroll to top when component mounts to prevent auto-scroll to bottom
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  if (!ready) return null

  const caseLimitReached = totalCaseCount >= EVAL_CASE_LIMIT

  return (
    <>
      {showNavigation && (
        <div className="w-full mb-8 space-y-3">
          <Breadcrumb className="mt-4 mb-2">
            <BreadcrumbList className="text-lg">
              <BreadcrumbItem>
                <Link to={`/workspaces/${workspaceId}`}>
                  {t("evaluation.evaluationTable.breadcrumb.workspaces")}
                </Link>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <Link to={`/workspaces/${workspaceId}/agents`}>
                  {t("evaluation.evaluationTable.breadcrumb.agents")}
                </Link>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <AgentBreadcrumbSelector
                  agents={agents}
                  currentAgent={currentAgent}
                  onAgentSelect={handleAgentSelect}
                  onCreateNewAgent={handleCreateNewAgent}
                />
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          {currentAgent && (
            <PageBreadcrumbSelector
              workspaceId={workspaceId!}
              agentId={currentAgent.id.toString()}
              currentPage="evaluation"
            />
          )}
          {/* Hidden conceptual snapshot fields for breadcrumb/toolbar */}
          <CopilotHiddenField
            fieldKey="breadcrumb.agentSelector.selected"
            value={currentAgent?.name || ""}
          />
        </div>
      )}

      {/* Main content container */}
      <div className="w-full min-w-0 mb-10">
        <div className="flex min-w-0 items-center justify-between mb-4">
          <EvaluationTableHeader
            onRunAll={handleRunAll}
            onRunSelected={handleRunSelected}
            onUploadCSV={handleOpenCsvDialog}
            runAllLoading={runAllLoading && !runSelectedLoading}
            runSelectedLoading={runSelectedLoading}
            onCopySelected={handleCopySelected}
            onDeleteSelected={handleDeleteSelected}
            selectedCount={selectedIds.length}
            deleteLoading={deleteLoading}
            runAllDisabled={
              !selectedEvaluator ||
              !selectedAgentHistory?.id ||
              runnablePairCount === 0 ||
              runSelectedLoading ||
              runAllLoading
            }
            runSelectedDisabled={
              !selectedEvaluator ||
              !selectedAgentHistory?.id ||
              selectedRunnablePairCount === 0 ||
              runSelectedLoading ||
              runAllLoading
            }
            runAllRunnableCount={runnablePairCount}
            runSelectedRunnableCount={selectedRunnablePairCount}
            onAddCase={() => setShowAddCaseModal(true)}
            caseLimitReached={caseLimitReached}
            currentAgent={currentAgent}
            agentHistoryState={agentHistoryState}
            evaluators={evaluators}
            selectedEvaluator={selectedEvaluator || undefined}
            testerSelectionDisabled={isDetailSaving}
            labels={labels}
            selectedLabelIds={selectedLabelIds}
            onSelectLabelIds={setSelectedLabelIds}
            onSelectEvaluator={handleSelectEvaluator}
            onManageLabels={() => setShowLabelsModal(true)}
            onManageEvaluators={() => setShowEvaluatorModal(true)}
          />
        </div>
        <div className="flex min-w-0 gap-4">
          <div className="min-w-0 flex-1">
            <div className="bg-white dark:bg-slate-950 rounded-xl shadow-xs border border-gray-200 dark:border-slate-800 p-6 relative min-w-0 overflow-hidden">
              {/* Hidden conceptual snapshot fields for modal open states */}
              <CopilotHiddenField
                fieldKey="modal.evaluator.open"
                value={showEvaluatorModal ? "true" : "false"}
              />
              <CopilotHiddenField
                fieldKey="modal.addCase.open"
                value={showAddCaseModal ? "true" : "false"}
              />
              <CopilotHiddenField
                fieldKey="evaluationTable.selectedCaseId"
                value={selectedCaseId || ""}
              />
              {loading ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                  {t("evaluation.evaluationTable.message.loading")}
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500 dark:text-red-400">
                  {t("evaluation.evaluationTable.message.fetchResultsError")}
                </div>
              ) : (
                <EvaluationCaseTable
                  cases={tableCases}
                  results={tableResults}
                  selectedEvaluator={selectedEvaluator}
                  evaluators={evaluators}
                  selectedCaseId={selectedCaseId}
                  selectedIds={selectedIds}
                  onSelectRow={handleSelectRow}
                  allSelected={allSelected}
                  isIndeterminate={isIndeterminate}
                  onSelectAll={handleSelectAll}
                  onOpenCase={handleOpenCaseDetail}
                />
              )}
              {caseLimitReached && (
                <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/40 dark:text-red-200">
                  {t("evaluation.evaluationTable.message.caseLimit", {
                    limit: EVAL_CASE_LIMIT.toLocaleString(),
                  })}
                </div>
              )}
              <AddCaseModal
                isOpen={showAddCaseModal}
                onClose={() => setShowAddCaseModal(false)}
                onSubmit={handleAddCaseSubmit}
                addCaseForm={addCaseForm}
                onChange={handleAddCaseChange}
                addCaseError={addCaseError}
                addCaseLoading={addCaseLoading}
              />
            </div>
          </div>
          <aside
            className={`hidden self-start shrink-0 overflow-hidden lg:block motion-reduce:transition-none transition-[width,min-width,opacity,transform] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              selectedCase
                ? "sticky top-4 h-[calc(100dvh-4rem)] min-w-[420px] w-[min(34vw,520px)] translate-x-0 opacity-100"
                : "min-w-0 w-0 translate-x-2 opacity-0"
            }`}
            aria-hidden={!selectedCase}
          >
            {selectedCase && (
              <CaseDetailModal
                key={`${selectedCase.id}-${selectedEvaluator?.id || "all"}`}
                isOpen={true}
                onClose={handleCloseCaseDetail}
                onCancel={handleCancelCaseDetail}
                caseData={selectedCase}
                evaluators={evaluators}
                labels={labels}
                initialEvaluator={selectedEvaluator || undefined}
                workspaceId={workspaceId}
                evaluationResult={currentDetailEvaluationResult}
                onFieldChange={handleCaseChangeEnhanced}
                onRunSingleCase={handleRunSingleCase}
                selectedAgentHistory={selectedAgentHistory}
                variant="panel"
                testerMode={selectedEvaluator ? "evaluation" : "assignment"}
                onDirtyChange={setHasDetailUnsavedChanges}
                onSavingChange={
                  isDetailDrawerMode ? undefined : setIsDetailSaving
                }
                onManageLabels={() => setShowLabelsModal(true)}
              />
            )}
          </aside>
        </div>
        <Sheet
          open={isDetailOpenOnMobile && !!selectedCase}
          onOpenChange={(open) => {
            if (open) {
              setIsDetailOpenOnMobile(true)
            } else {
              handleCloseCaseDetail()
            }
          }}
        >
          <SheetContent className="w-[92vw] gap-0 p-0 sm:max-w-[640px] [&>button:last-child]:hidden">
            {selectedCase && (
              <CaseDetailModal
                key={`mobile-${selectedCase.id}-${selectedEvaluator?.id || "all"}`}
                isOpen={true}
                onClose={handleCloseCaseDetail}
                onCancel={handleCancelCaseDetail}
                caseData={selectedCase}
                evaluators={evaluators}
                labels={labels}
                initialEvaluator={selectedEvaluator || undefined}
                workspaceId={workspaceId}
                evaluationResult={currentDetailEvaluationResult}
                onFieldChange={handleCaseChangeEnhanced}
                onRunSingleCase={handleRunSingleCase}
                selectedAgentHistory={selectedAgentHistory}
                variant="panel"
                testerMode={selectedEvaluator ? "evaluation" : "assignment"}
                onDirtyChange={setHasDetailUnsavedChanges}
                onSavingChange={
                  isDetailDrawerMode ? setIsDetailSaving : undefined
                }
                onManageLabels={() => setShowLabelsModal(true)}
              />
            )}
          </SheetContent>
        </Sheet>
        <EvaluatorModal
          evaluators={evaluators}
          refreshEvaluators={refreshEvaluators}
          onEvaluatorCreated={(_newEvaluator) => {
            // Add the new evaluator to the local state
            // This will be handled by the useEvaluationData hook
            refreshEvaluators()
          }}
          onEvaluatorUpdated={(_updatedEvaluator) => {
            // Update the evaluator in the local state
            // This will be handled by the useEvaluationData hook
            refreshEvaluators()
          }}
          open={showEvaluatorModal}
          onClose={() => setShowEvaluatorModal(false)}
        />
        <ManageCaseLabelsModal
          isOpen={showLabelsModal}
          workspaceId={workspaceId}
          labels={labels}
          onClose={() => setShowLabelsModal(false)}
          onLabelsChanged={() => {
            refreshLabels()
            fetchCases({ searchParams: getCaseSearchParams() })
          }}
        />
      </div>
      <CsvPreviewModal
        isOpen={showCsvDialog}
        rows={csvPreviewRows}
        errors={csvPreviewErrors.concat(
          csvWouldExceedLimit
            ? [
                t("evaluation.evaluationTable.message.caseLimit", {
                  limit: EVAL_CASE_LIMIT.toLocaleString(),
                }),
              ]
            : []
        )}
        file={csvFile}
        onCancel={handleCancelCsvPreview}
        onConfirm={handleConfirmCsvUpload}
        onFileSelected={handleUploadCSV}
        uploading={csvUploading}
        // Disable upload if limit would be exceeded
        uploadDisabled={csvWouldExceedLimit}
      />
      <CopilotHiddenField
        fieldKey="modal.csvPreview.open"
        value={showCsvDialog ? "true" : "false"}
      />
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        selectedCount={selectedIds.length}
        loading={deleteLoading}
      />
      <CopilotHiddenField
        fieldKey="modal.deleteConfirm.open"
        value={showDeleteConfirm ? "true" : "false"}
      />
      <AlertDialog
        open={showUnsavedDetailDialog}
        onOpenChange={(open) => {
          if (!open) handleCancelDiscardDetailChanges()
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("evaluation.evaluationTable.detail.unsaved.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                pendingEvaluatorSelection !== undefined
                  ? "evaluation.evaluationTable.detail.unsaved.descriptionTester"
                  : "evaluation.evaluationTable.detail.unsaved.description"
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDiscardDetailChanges}>
              {t("evaluation.evaluationTable.detail.unsaved.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscardDetailChanges}>
              {t("evaluation.evaluationTable.detail.unsaved.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default React.memo(EvaluationTablePage)
